"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function Expertbiography() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="257459" id="expertbiography" className="bg-background text-foreground py-20 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683668/img/grid-me.png')]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 gap-12 items-center max-w-4xl mx-auto">
          <div ref={ref} className={`flex flex-col justify-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="mb-6">
              <span className="text-[32px] font-default text-primary tracking-wider block font-bold">
                The Architect
              </span>
            </div>
            
            <h2 className="text-4xl md:text-[48px] leading-tight font-default text-foreground mb-8 font-bold">
              David Scott Linthicum
            </h2>
            
            <div className="space-y-6 text-lg text-muted-foreground font-default leading-relaxed">
              <p>
                As a globally recognized visionary in cloud computing and AI, David Scott Linthicum has spent decades defining the architectural frameworks that power modern enterprise. With over 13 published books and a prolific presence across major tech publications, he serves as a primary guide for organizations navigating the complexities of digital transformation.
              </p>
              <p>
                Through his popular YouTube channels and extensive media hub, David provides actionable insights into the future of technology. His expertise isn't just theoretical; it's forged in the trenches of high-stakes consulting, white paper development, and project mentoring, ensuring that every strategy is grounded in real-world scalability and innovation.
              </p>
            </div>

            <div className="mt-10">
              <Link to="/LibraryPage">
                <Button variant="outline" size="lg" className="rounded-md px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors font-default font-semibold">
                  Explore the Library
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}